import { useEffect, useState, useMemo, useCallback } from "react";
import CookiesServices from "../../services/CookiesService";
import defaultProfilePic from "../../assets/default-profile.jpg";
import mdrt from "../../../src/assets/mdrt.jpg";
import mdrt_cot from "../../../src/assets/mdrt_cot.jpg";
import mdrt_tot from "../../../src/assets/mdrt_tot.jpg";
import limr_iqa from "../../../src/assets/limr_iqa.jpg";
import lim from "../../../src/assets/lim.jpg";

const useAgentProfile = () => {
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);

  const [photoUrl, setPhotoUrl] = useState(defaultProfilePic);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);

  const cookiesServices = useMemo(() => new CookiesServices(), []);
  cookiesServices.validateLogin();

  const [level, setLevel] = useState(cookiesServices.get("Level"));
  const [kpiMode, setKpiMode] = useState(cookiesServices.get("kpiMode"));
  const [kpiUser, setKpiUser] = useState(cookiesServices.get("kpiUser"));
  const [accessToken] = useState(cookiesServices.get("AccessToken"));
  const [UserName] = useState(cookiesServices.get("UserName"));
  const isSameUser = kpiUser === UserName;
  var headers = cookiesServices.getHeaders();

  // Map achievement codes to imported images
  const achievementImages = {
    MDRT: mdrt,
    MDRT_COT: mdrt_cot,
    MDRT_TOT: mdrt_tot,
    LIMRL_IQA: limr_iqa,
    LIM: lim,
  };

  // Watch for Level changes
  useEffect(() => {
    const initialLevel = cookiesServices.get("Level");
    setLevel(initialLevel);

    const interval = setInterval(() => {
      const currentLevel = cookiesServices.get("Level");
      if (currentLevel !== level) setLevel(currentLevel);
    }, 1000);

    return () => clearInterval(interval);
  }, [level]);

  const handleError = (status) => {
    if (status === 401) {
      setFeedback({
        message: "Your session has expired. Please sign in again to continue.",
        type: "expire",
      });
      cookiesServices.clearLogin();
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}`;
    } else if (status === 500) {
      setFeedback({
        message: "Internal Server Error. Please try again later.",
        type: "error",
      });
    } else {
      setFeedback({
        message: "Failed to fetch data. Please try again later.",
        type: "error",
      });
    }
  };

  // Get achievement images from static imports
  const getAchievementImages = (achievementString) => {
    if (!achievementString) return [];
    return achievementString
      .split(",")
      .map(a => a.trim())
      .map(code => achievementImages[code] || null)
      .filter(Boolean);
  };

  const fetchProfile = useCallback(async () => {
    if (!kpiUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(cookiesServices.toUrl('v1', `/agentprofile/details`), {
        method: 'GET',
        headers,
      });

      if (!res.ok) {
        handleError(res.status);
        setLoading(false);
        return;
      }

      const result = await res.json();

      // SUCCESS — set data
      if (result.status === "success" && result.data) {
        setProfile(result.data);
        setFormData(result.data);
        fetchProfilePhoto(kpiUser);
      } else if (result.status === "na") {
        setProfile(null);
        setFormData({ __unauthorized: true });
        setFeedback({
          message: "You are not authorized to update this eProfile.",
          type: "error",
        });
        setPhotoUrl(defaultProfilePic);
      } else {
        setFeedback({
          message: "Failed to load profile.",
          type: "error",
        });
      }
    } catch (err) {
      setFeedback({
        message: "Error loading profile.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [accessToken, UserName, kpiUser, level, kpiMode]);

  // Fetch profile photo with cache-buster
  const fetchProfilePhoto = async (agentCode) => {
    if (!agentCode) return;

    setPhotoLoading(true);
    try {
      const timestamp = new Date().toISOString();
      const res = await fetch(
        cookiesServices.toUrl(
          'v1',
          `/agentprofile/profile-photo/${agentCode}?dt=${encodeURIComponent(timestamp)}`
        )
      );
      if (!res.ok) throw new Error();

      const blob = await res.blob();
      if (blob && blob.type.startsWith("image/")) {
        URL.revokeObjectURL(photoUrl);
        setPhotoUrl(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.warn("Profile photo fetch failed, using default.");
      setPhotoUrl(defaultProfilePic);
    } finally {
      setPhotoLoading(false);
    }
  };

  // Auto refresh when kpiUser changes
  useEffect(() => {
    const handleLevelChange = () => {
      const newLevel = cookiesServices.get("Level");
      const newKpiUser = cookiesServices.get("kpiUser");
      const newKpiMode = cookiesServices.get("kpiMode");

      setLevel(newLevel);
      setKpiUser(newKpiUser);
      setKpiMode(newKpiMode);

      // Reset old values before reload
      setProfile(null);
      setFormData({});
      setPhotoUrl(defaultProfilePic);

      fetchProfile();
    };

    window.addEventListener("level_changed", handleLevelChange);

    return () => window.removeEventListener("level_changed", handleLevelChange);
  }, [fetchProfile, cookiesServices]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Save profile & photo
  const handleSave = async () => {
    if (!isSameUser) {
      setFeedback({
        message: "You are not authorized to update this profile.",
        type: "error",
      });
      return;
    }

    setSaving(true);
    setFeedback(null);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] != null && key !== "__unauthorized") {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedPhotoFile) {
        formDataToSend.append("profile_photo", selectedPhotoFile);
      }

      const res = await fetch(cookiesServices.toUrl('v1', `/agentprofile/save`), {
        method: 'POST',
        headers: cookiesServices.getHeadersFormData(),
        body: formDataToSend,
      });

      const result = await res.json();

      if (res.status === 200) {
        setProfile(result.data);
        if (selectedPhotoFile) {
          setPhotoUrl(URL.createObjectURL(selectedPhotoFile));
          setSelectedPhotoFile(null);
        }
        setFeedback({ message: "Profile saved successfully!", type: "success" });
      } else {
        handleError(res.status);
        setFeedback({
          message: "Failed to save profile.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error saving profile");
      setFeedback({
        message: "Error saving profile.",
        type: "error",
      });
    } finally {
      setSaving(false);
      setEditing(null);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleImageError = () => setPhotoUrl(defaultProfilePic);

  return {
    profile,
    formData,
    saving,
    editing,
    feedback,
    loading,
    setEditing,
    setFeedback,
    handleChange,
    handleSave,
    setFormData,
    photoUrl,
    photoLoading,
    setSelectedPhotoFile,
    setPhotoUrl,
    handleImageError,
    kpiUser,
    UserName,
    isSameUser,
    getAchievementImages,
  };
};

export default useAgentProfile;
